import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Check, Loader2, AlertCircle,
  Building2, Activity, Radar, GitBranch, Layers, LineChart,
  ShieldCheck, Cpu, Gauge
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import useRevealObserver from '../hooks/useRevealObserver';
import AITestimonyBlock from '../components/AITestimonyBlock';
import { submitLead } from '../lib/api';

/**
 * /ceo — Landing page for AVATAR B: B2B / Industry / Engineering companies.
 * Persona: CEOs, VP Growth, founders of B2B & industrial firms.
 * Design brief: Boardroom aesthetic — Navy dominant, steel-gray/silver accents,
 * grid/flowchart visuals, corporate reliability and engineering discipline.
 * Lead source: 'ceo-landing' (already in admin labels).
 */
const Ceo = () => {
  useRevealObserver();
  const { toast } = useToast();

  const [form, setForm] = useState({
    fullName: '',
    role: '',
    company: '',
    email: '',
    phone: '',
    mainGoal: '',
    companySize: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(null);
  const errorRef = useRef(null);

  const handle = (key) => (e) => {
    setForm({ ...form, [key]: e.target ? e.target.value : e });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'יש להזין שם מלא';
    if (!form.role.trim()) errs.role = 'יש להזין תפקיד בארגון';
    if (!form.company.trim()) errs.company = 'יש להזין שם חברה או קישור לאתר';
    if (!form.email.trim()) errs.email = 'יש להזין דוא"ל עסקי';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'כתובת דוא"ל לא תקינה';
    if (!form.phone.trim()) errs.phone = 'יש להזין מספר טלפון';
    if (!form.mainGoal.trim()) errs.mainGoal = 'נודה לתשובה קצרה על היעד העסקי';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast({ title: 'יש להשלים את השדות המסומנים' });
      setTimeout(() => errorRef.current?.focus(), 50);
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({
        fullName: form.fullName,
        role: form.role,
        company: form.company,
        email: form.email,
        phone: form.phone,
        challenge: form.mainGoal,
        improve: form.companySize || undefined,
        source: 'ceo-landing',
      });
      setSubmitted(true);
    } catch (err) {
      toast({
        title: 'שגיאה בשליחת הטופס',
        description: err?.response?.data?.detail || 'נסו שוב בעוד רגע',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="pt-20">
      {/* ============ SECTION 1 · HERO ============ */}
      <section
        className="relative overflow-hidden bg-navy text-cream"
        aria-labelledby="ceo-hero-heading"
        data-testid="ceo-hero"
      >
        {/* Grid background */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(201,169,97,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.5) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="reveal max-w-4xl">
            <div
              className="inline-flex items-center gap-2 text-xs tracking-[0.22em] font-semibold text-bronze mb-6 uppercase"
              data-testid="ceo-eyebrow"
            >
              <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
              <span>Corporate · B2B · Industry</span>
            </div>
            <h1
              id="ceo-hero-heading"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.15] mb-8"
            >
              אתם מנהלים אופרציה מורכבת בסטנדרט הגבוה ביותר.<br className="hidden md:block" />
              <span className="text-bronze">
                {' '}הגיע הזמן שגם מערך הצמיחה שלכם יעבוד באותה רמה.
              </span>
            </h1>
            <p className="text-lg lg:text-xl leading-relaxed text-cream/85 max-w-3xl mb-10">
              חברות תעשייה, הנדסה ו-B2B זקוקות ליותר משיווק — הן זקוקות לתשתית.
              אנו מביאים את הסטנדרטים הקפדניים של ניהול פרויקטים לאומיים אל עולם
              הדיגיטל, ובונים עבורכם אדריכלות עסקית שמייצרת הזדמנויות וצמיחה
              אסטרטגית ברורה.
            </p>
            <button
              type="button"
              onClick={scrollToForm}
              className="btn-cta inline-flex items-center gap-3 bg-bronze text-navy px-8 py-4 text-base font-medium hover:bg-bronze-600 transition-colors"
              data-testid="ceo-hero-cta"
            >
              <span>קביעת פגישת אסטרטגיה ואבחון מערכות</span>
              <ArrowLeft className="w-5 h-5" aria-hidden="true" />
            </button>

            {/* Corporate trust rail — mini logo strip */}
            <div className="mt-14 pt-8 border-t border-cream/15">
              <p className="text-xs tracking-widest text-cream/50 mb-4 uppercase">
                Trusted operating partners
              </p>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-cream/75">
                <span>משרד ראש הממשלה</span>
                <span className="text-cream/25">•</span>
                <span>שירות בתי הסוהר</span>
                <span className="text-cream/25">•</span>
                <span>מערכת הבנקאות</span>
                <span className="text-cream/25">•</span>
                <span>פרויקטי תשתית סולאריים לאומיים</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI TESTIMONY (shared GEO block) */}
      <AITestimonyBlock />

      {/* ============ SECTION 2 · DNA / STATUS ============ */}
      <section className="py-24 lg:py-28 bg-cream" aria-labelledby="ceo-dna-heading">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Text — right side in RTL */}
            <div className="lg:col-span-7 reveal">
              <p className="eyebrow text-bronze mb-4">The Operational Standard</p>
              <h2
                id="ceo-dna-heading"
                className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6"
              >
                התאמת האקו-סיסטם הדיגיטלי<br className="hidden md:block" /> ל-DNA של החברה שלכם.
              </h2>
              <div className="space-y-5 text-navy/80 text-base lg:text-lg leading-relaxed">
                <p>
                  הארגון שלכם בנוי על תהליכים סדורים. מניהול שרשראות אספקה ועד
                  בקרת איכות — הכל אצלכם מנוהל, מדיד, ומבוסס על נתונים.
                </p>
                <p>
                  כשהחברה צומחת ופונה לדיגיטל כדי לייצר עסקאות B2B מורכבות או
                  לפרוץ לשווקים חדשים, היא חייבת מעטפת שפועלת בדיוק באותה שפה.
                  אתם זקוקים למערכת מסונכרנת אחת שמחברת בין חזון הנהלת החברה,
                  לבין השורה התחתונה של צוות המכירות.
                </p>
              </div>
            </div>

            {/* KPI/systems icons — left side */}
            <div className="lg:col-span-5 reveal">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Gauge, label: 'KPI מדידים', sub: 'לא חשיפות — תפוקות' },
                  { icon: Radar, label: 'בקרה יומית', sub: 'ניטור לאורך המשפך' },
                  { icon: LineChart, label: 'צמיחה יציבה', sub: 'לא ספייקים אקראיים' },
                  { icon: GitBranch, label: 'סנכרון מערכות', sub: 'שיווק ↔ מכירות' },
                ].map((it) => {
                  const Icon = it.icon;
                  return (
                    <div
                      key={it.label}
                      className="bg-white border border-navy/10 p-6 hover:border-bronze/50 transition-colors"
                    >
                      <div className="w-10 h-10 bg-navy/5 flex items-center justify-center mb-4">
                        <Icon className="w-5 h-5 text-navy" aria-hidden="true" />
                      </div>
                      <p className="font-serif text-lg text-navy mb-1">{it.label}</p>
                      <p className="text-xs text-navy/60 leading-relaxed">{it.sub}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 3 · TURN-KEY MECHANISM ============ */}
      <section className="py-24 lg:py-28 bg-white" aria-labelledby="ceo-turnkey-heading">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="reveal max-w-3xl mb-16">
            <p className="eyebrow text-bronze mb-4">The Mechanism</p>
            <h2
              id="ceo-turnkey-heading"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight"
            >
              תכלול מערכות מלא (Turn-Key) לצמיחה עסקית.
            </h2>
          </div>

          <ol className="grid lg:grid-cols-3 gap-6 lg:gap-4 relative" role="list">
            {[
              {
                num: '01',
                icon: Layers,
                title: 'אדריכלות ואסטרטגיה עסקית',
                text: 'לפני כל פעולה אופרטיבית, אנו חוקרים את מחזור המכירה המורכב שלכם (B2B). מאפיינים את תהליך קבלת ההחלטות של הלקוחות ובונים "מפת דרכים" שיווקית מותאמת ליעדי החברה.',
              },
              {
                num: '02',
                icon: Cpu,
                title: '"קבלן ראשי" לדיגיטל',
                text: 'עסק בסדר הגודל שלכם לא צריך לנהל אסופה של ספקים מפוזרים. אנו מתפקדים כהנהלת השיווק והצמיחה שלכם — מתכללים קמפיינים, תוכן ואוטומציות, ומוודאים שהמערך פועל כיחידה מסונכרנת.',
              },
              {
                num: '03',
                icon: Activity,
                title: 'בקרה, תפוקות והתייעלות (ROI)',
                text: 'אנו מדברים בשפה שלכם. המדידה אינה מתמקדת ב"חשיפות" אלא בתפוקות עסקיות. אנו מנטרים את המערכת ברמה יומית כדי לוודא שמשאבי החברה מנוצלים ביעילות מקסימלית ומייצרים לידים איכותיים ובשלים.',
              },
            ].map((b, i, arr) => {
              const Icon = b.icon;
              const isLast = i === arr.length - 1;
              return (
                <li
                  key={b.num}
                  className="relative bg-cream border border-navy/10 p-8 lg:p-9 lift-card reveal"
                  style={{ transitionDelay: `${i * 90}ms` }}
                  data-testid={`ceo-turnkey-block-${i}`}
                >
                  {/* Step connector arrow — hidden on last item */}
                  {!isLast && (
                    <ArrowLeft
                      className="hidden lg:block absolute top-1/2 -translate-y-1/2 -left-5 w-6 h-6 text-bronze"
                      aria-hidden="true"
                    />
                  )}
                  <div className="flex items-center justify-between mb-5">
                    <span className="font-serif text-3xl text-bronze">{b.num}</span>
                    <Icon className="w-6 h-6 text-navy/60" aria-hidden="true" />
                  </div>
                  <h3 className="font-serif text-2xl text-navy mb-3 leading-snug">{b.title}</h3>
                  <p className="text-navy/70 text-sm lg:text-base leading-relaxed">{b.text}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ============ SECTION 4 · AUTHORITY / ENGINEERING DNA ============ */}
      <section
        className="py-24 lg:py-28 relative overflow-hidden"
        style={{
          background:
            'linear-gradient(180deg, #2a2f3a 0%, #1e222c 100%)',
        }}
        aria-labelledby="ceo-authority-heading"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12">
          <div className="reveal max-w-3xl mb-14">
            <p className="text-xs tracking-[0.22em] font-semibold text-bronze mb-4 uppercase">
              Authority · Engineering DNA
            </p>
            <h2
              id="ceo-authority-heading"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight"
            >
              שפה משותפת של אמינות וניהול סיכונים.
            </h2>
          </div>

          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <p className="lg:col-span-7 text-cream/80 text-base lg:text-lg leading-relaxed reveal">
              הייחודיות של <strong className="text-cream">GAL BLIZOVSKY</strong> טמונה
              בסינרגיה שבין הבנה פסיכולוגית-דיגיטלית עמוקה, לבין ניסיון ניהולי והנדסי
              מהשורה הראשונה. עם רקע של הקמה וניהול פרויקטים סולאריים לאומיים ועבודה מול
              הגופים המחמירים במשק (משרד ראש הממשלה, שב"ס, בנקים) — אנו מביאים את
              החספוס, הדיוק וניהול הסיכונים הישירות אל מערך הדיגיטל שלכם. שיווק מתוכנן
              היטב, ללא מקום לניחושים.
            </p>

            <ul className="lg:col-span-5 space-y-4 reveal" style={{ transitionDelay: '120ms' }}>
              {[
                { k: '400M+', l: 'ש״ח מכירות דיגיטליות שנוצרו ללקוחות' },
                { k: '400%', l: 'צמיחה ב-18 חודש (אחד ממקרי הבוחן)' },
                { k: '1,000+', l: 'אנשי מקצוע שלמדו מהשיטה' },
                { k: '5 שנים', l: 'ניסיון תפעולי מוכח' },
              ].map((s) => (
                <li
                  key={s.k}
                  className="flex items-baseline gap-4 pb-4 border-b border-cream/10"
                >
                  <span className="font-serif text-3xl lg:text-4xl text-bronze min-w-[7rem]">
                    {s.k}
                  </span>
                  <span className="text-cream/75 text-sm lg:text-base">{s.l}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ SECTION 5 · VALUE PROPOSITION ============ */}
      <section className="py-24 lg:py-28 bg-cream" aria-labelledby="ceo-value-heading">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center reveal">
          <p className="eyebrow text-bronze mb-4">Growth Partnership</p>
          <h2
            id="ceo-value-heading"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-8"
          >
            מחפשים שותפי צמיחה,<br className="hidden md:block" /> לא רק לקוחות.
          </h2>
          <p className="text-navy/75 text-base lg:text-lg leading-relaxed">
            אנו עובדים עם חברות הנדסה, תעשייה, קבלנות וארגוני B2B בעלי מוצר מצוין
            ותשתיות עובדות. אם הארגון שלכם בשל לשלב הבא, ואתם מחפשים גוף שייקח בעלות
            מלאה על האדריכלות הדיגיטלית שלכם ויבנה עבורכם מנוע צמיחה אמין — אנו
            מזמינים אתכם לבחון שותפות.
          </p>
        </div>
      </section>

      {/* ============ SECTION 6 · B2B QUALIFICATION FORM ============ */}
      <section
        ref={formRef}
        className="py-24 lg:py-28 bg-white border-t border-navy/10"
        aria-labelledby="ceo-form-heading"
        data-testid="ceo-form-section"
      >
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          {submitted ? (
            <div className="text-center py-8" data-testid="ceo-form-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-bronze/15 text-bronze flex items-center justify-center mb-5">
                <Check className="w-7 h-7" aria-hidden="true" />
              </div>
              <h2 className="font-serif text-3xl lg:text-4xl text-navy mb-3">
                הבקשה נקלטה
              </h2>
              <p className="text-navy/70 text-base lg:text-lg">
                נחזור אליכם תוך 24 שעות עסקיות לקביעת פגישת אבחון אישית.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-8 text-sm text-navy hover:text-bronze transition-colors"
              >
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                חזרה לעמוד הבית
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} noValidate data-testid="ceo-form">
              <div className="text-center mb-10">
                <p className="eyebrow text-bronze mb-4">B2B Qualification</p>
                <h2 id="ceo-form-heading" className="font-serif text-3xl lg:text-4xl text-navy mb-4">
                  תיאום פגישת אבחון מערכות
                </h2>
                <p className="text-navy/70 text-base leading-relaxed max-w-2xl mx-auto">
                  הפגישה מיועדת למנכ"לים, סמנכ"לים ובעלי חברות. מטרתה להבין את
                  מנועי הצמיחה של הארגון שלכם, ולבחון יחד האם תשתית הניהול השיווקית
                  של GAL BLIZOVSKY תואמת את יעדי החברה.
                </p>
              </div>

              {Object.keys(errors).length > 0 && (
                <div
                  ref={errorRef}
                  tabIndex={-1}
                  role="alert"
                  className="mb-6 p-4 border border-destructive/40 bg-destructive/5 flex items-start gap-3"
                  data-testid="ceo-form-errors"
                >
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <p className="font-medium text-destructive">יש להשלים את השדות הבאים:</p>
                    <ul className="text-sm text-destructive/90 mt-1 list-disc pr-5">
                      {Object.entries(errors).map(([f, m]) => <li key={f}>{m}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="ceo-name" className="text-navy text-sm mb-2 block">
                    שם מלא <span className="text-bronze">*</span>
                  </Label>
                  <Input
                    id="ceo-name"
                    value={form.fullName}
                    onChange={handle('fullName')}
                    autoComplete="name"
                    className="input-clean h-12"
                    aria-invalid={!!errors.fullName}
                    data-testid="ceo-input-fullName"
                  />
                </div>
                <div>
                  <Label htmlFor="ceo-role" className="text-navy text-sm mb-2 block">
                    תפקיד בארגון <span className="text-bronze">*</span>
                  </Label>
                  <Input
                    id="ceo-role"
                    value={form.role}
                    onChange={handle('role')}
                    autoComplete="organization-title"
                    className="input-clean h-12"
                    placeholder="מנכ״ל / סמנכ״ל / בעלים"
                    aria-invalid={!!errors.role}
                    data-testid="ceo-input-role"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="ceo-company" className="text-navy text-sm mb-2 block">
                    שם החברה / קישור לאתר <span className="text-bronze">*</span>
                  </Label>
                  <Input
                    id="ceo-company"
                    value={form.company}
                    onChange={handle('company')}
                    autoComplete="organization"
                    className="input-clean h-12"
                    placeholder="שם החברה או example.com"
                    aria-invalid={!!errors.company}
                    data-testid="ceo-input-company"
                  />
                </div>
                <div>
                  <Label htmlFor="ceo-email" className="text-navy text-sm mb-2 block">
                    דוא״ל עסקי <span className="text-bronze">*</span>
                  </Label>
                  <Input
                    id="ceo-email"
                    type="email"
                    value={form.email}
                    onChange={handle('email')}
                    autoComplete="email"
                    dir="ltr"
                    className="input-clean h-12"
                    aria-invalid={!!errors.email}
                    data-testid="ceo-input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="ceo-phone" className="text-navy text-sm mb-2 block">
                    טלפון <span className="text-bronze">*</span>
                  </Label>
                  <Input
                    id="ceo-phone"
                    type="tel"
                    value={form.phone}
                    onChange={handle('phone')}
                    autoComplete="tel"
                    dir="ltr"
                    className="input-clean h-12"
                    aria-invalid={!!errors.phone}
                    data-testid="ceo-input-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="ceo-goal" className="text-navy text-sm mb-2 block">
                    מהו היעד העסקי המרכזי של החברה לשנה הקרובה? <span className="text-bronze">*</span>
                  </Label>
                  <Textarea
                    id="ceo-goal"
                    value={form.mainGoal}
                    onChange={handle('mainGoal')}
                    rows={4}
                    className="input-clean"
                    placeholder="לדוגמה: פריצה לשוק ה-Enterprise, הכפלת פייפליין המכירות, כניסה לשווקים חדשים…"
                    aria-invalid={!!errors.mainGoal}
                    data-testid="ceo-input-mainGoal"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="ceo-size" className="text-navy text-sm mb-2 block">
                    היקף פעילות מוערך / גודל חברה <span className="text-navy/40 text-xs">(רשות)</span>
                  </Label>
                  <select
                    id="ceo-size"
                    value={form.companySize}
                    onChange={handle('companySize')}
                    className="input-clean h-12 w-full px-3"
                    data-testid="ceo-input-size"
                  >
                    <option value="">בחרו טווח</option>
                    <option value="1-10 עובדים">1–10 עובדים</option>
                    <option value="10-50 עובדים">10–50 עובדים</option>
                    <option value="50-200 עובדים">50–200 עובדים</option>
                    <option value="200+ עובדים">200+ עובדים</option>
                    <option value="מחזור 5-20M ₪">מחזור 5–20 מ׳ ש״ח</option>
                    <option value="מחזור 20M+ ₪">מחזור 20+ מ׳ ש״ח</option>
                  </select>
                </div>
              </div>

              <div className="mt-8 text-center">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-cta inline-flex items-center justify-center gap-3 bg-navy text-cream px-10 py-4 text-base font-medium hover:bg-navy-700 transition-colors disabled:opacity-60"
                  data-testid="ceo-form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                      <span>שולח…</span>
                    </>
                  ) : (
                    <>
                      <Building2 className="w-5 h-5" aria-hidden="true" />
                      <span>שליחת בקשה לתיאום פגישה אישית</span>
                    </>
                  )}
                </button>
                <p className="text-xs text-navy/50 mt-4">
                  הפרטים נשמרים אצלנו בלבד. לא ישותפו עם צד שלישי.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Ceo;
